import api from '../../../api/client';
import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Card,
  Row,
  Col,
  Popconfirm,
  message,
  Tag,
  Tooltip,
  Select,
  Statistic,
  Modal,
  Typography,
  Form,
  Radio,
  ConfigProvider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  EyeOutlined,
  LockOutlined,
  UserOutlined,
  TeamOutlined,
  ManOutlined,
  WomanOutlined,
  MailOutlined,
  DeploymentUnitOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import config from "../../../config";
import { usePermissions } from "../../../permissions";
import UserForm from "./UserForm";
import { useHistory } from "react-router-dom";
import ProTable from "@ant-design/pro-table";
import enUS from "antd/lib/locale/en_US";
import { Link } from "react-router-dom";

const { Text } = Typography;

const UserList = () => {
  const history = useHistory();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [roleFilter, setRoleFilter] = useState(null);
  const [departmentFilter, setDepartmentFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [genderFilter, setGenderFilter] = useState(null);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkActionModal, setBulkActionModal] = useState({
    visible: false,
    type: null,
  });
  const [bulkForm] = Form.useForm();

  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;
  const { createPermission, editPermission, deletePermission } =
    usePermissions();

  const fetchRoles = async () => {
    try {
      const res = await api.get(`/roles`);
      setRoles(res.data);
    } catch (err) {
      console.error("Roles fetch error:", err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await api.get(`/departments`);
      setDepartments(res.data);
    } catch (err) {
      console.error("Departments fetch error:", err);
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText) params.search = searchText;
      if (roleFilter) params.role = roleFilter;
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      if (genderFilter) params.gender = genderFilter;
      if (departmentFilter) params.department = departmentFilter;

      const { data } = await api.get(`/users-filter`, { params });
      setUsers(data);
    } catch (error) {
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [
    apiUrl,
    searchText,
    roleFilter,
    statusFilter,
    genderFilter,
    typeFilter,
    departmentFilter,
  ]);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchDepartments();
  }, [fetchUsers]);

  // Statistics (now computed from `users` directly)
  const totalUsers = users.length;
  const totalAdmins = users.filter((u) => u.employee === 0).length;
  const totalEmployees = users.filter((u) => u.employee === 1).length;
  const totalActive = users.filter((u) => u.is_active === "Active").length;
  const totalInactive = users.filter((u) => u.is_active === "Inactive").length;
  const totalMale = users.filter((u) => u.gender === "Male").length;
  const totalFemale = users.filter((u) => u.gender === "Female").length;
  const totalDepartments = departments.length;

  // Single actions (unchanged)
  const handleArchive = async (id, name) => {
    try {
      await api.put(`/user/${id}/archive`);
      message.success(`${name} archived successfully`);
      fetchUsers();
      setSelectedRowKeys([]);
    } catch (error) {
      message.error("Archive failed");
    }
  };

  const handleResetPassword = async (id, name) => {
    try {
      await api.post(`/user/${id}/reset-password`);
      message.success(`Password reset link sent to ${name}`);
    } catch (error) {
      message.error("Password reset failed");
    }
  };

  const handleView = (user) => {
    history.push(`/users/details/${user.id}`);
  };

  // Bulk actions (unchanged)
  const openBulkModal = (type) => {
    bulkForm.resetFields();
    setBulkActionModal({ visible: true, type });
  };

  const executeBulkAction = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select at least one user");
      return;
    }
    const { type } = bulkActionModal;
    try {
      let payload = { userIds: selectedRowKeys };
      if (type === "gender") {
        const { gender } = await bulkForm.validateFields();
        payload.gender = gender;
        await api.post(`/users/bulk-update-gender`, payload);
        message.success(`Gender updated for ${selectedRowKeys.length} users`);
      } else if (type === "role") {
        const { role } = await bulkForm.validateFields();
        // payload.employee = role === "admin" ? 0 : 1;
        payload.employee = role; // send role ID instead of employee flag
        await api.post(`/users/bulk-update-role`, payload);
        message.success(`Role updated for ${selectedRowKeys.length} users`);
      } else if (type === "status") {
        const { status } = await bulkForm.validateFields();
        payload.is_active = status;
        await api.post(`/users/bulk-update-status`, payload);
        message.success(`Status updated for ${selectedRowKeys.length} users`);
      } else if (type === "reset") {
        await api.post(`/users/bulk-reset-password`, payload);
        message.success(
          `Password reset sent to ${selectedRowKeys.length} users`,
        );
      } else if (type === "message") {
        const { subject, messageText } = await bulkForm.validateFields();
        payload.subject = subject;
        payload.message = messageText;
        await api.post(`/users/bulk-send-message`, payload);
        message.success(`Message sent to ${selectedRowKeys.length} users`);
      }
      setBulkActionModal({ visible: false, type: null });
      fetchUsers();
      setSelectedRowKeys([]);
    } catch (error) {
      message.error(error.response?.data?.message || "Bulk action failed");
    }
  };

  // Export functions (unchanged, use `users` directly)
  const exportToCSV = () => {
    const headers = [
      "ID",
      "Name",
      "Email",
      "Phone",
      "Role",
      "Status",
      "Gender",
      "Created",
    ];
    const rows = users.map((u) => [
      u.id,
      `${u.firstname} ${u.lastname}`,
      u.email,
      u.phone_number,
      u.employee === 0 ? "Admin" : "Employee",
      u.is_active,
      u.gender || "N/A",
      dayjs(u.createdAt).format("YYYY-MM-DD"),
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8",
    });
    saveAs(blob, `users_${dayjs().format("YYYYMMDD_HHmmss")}.csv`);
    message.success("CSV exported");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        ["ID", "Name", "Email", "Phone", "Role", "Status", "Gender", "Created"],
      ],
      body: users.map((u) => [
        u.id,
        `${u.firstname} ${u.lastname}`,
        u.email,
        u.phone_number,
        u.employee === 0 ? "Admin" : "Employee",
        u.is_active,
        u.gender || "N/A",
        dayjs(u.createdAt).format("YYYY-MM-DD"),
      ]),
    });
    doc.save(`users_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`);
    message.success("PDF exported");
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "firstname",
      key: "name",
      render: (_, record) => (
        // <Button type="link" onClick={() => handleView(record)}>
        //   {record.firstname} {record.lastname}
        // </Button>
        <Link to={`/users/details/${record.id}`}>
          {record.firstname} {record.lastname}
        </Link>
      ),
      sorter: (a, b) => a.firstname.localeCompare(b.firstname),
    },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Phone", dataIndex: "phone_number", key: "phone" },
    {
      title: "Role",
      dataIndex: "roleName",
      key: "roleName",
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      filters: true,
      valueEnum: {
        Male: { text: "Male", status: "Success" },
        Female: { text: "Female", status: "Warning" },
      },
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "status",
      filters: true,
      valueEnum: {
        Active: { text: "Active", status: "Success" },
        Inactive: { text: "Inactive", status: "Error" },
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "created",
      render: (date) => dayjs(date).format("DD MMM YYYY"),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(record)} />
          </Tooltip>
          {editPermission && (
            <Tooltip title="Edit">
              <Button size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditingUser(record);
                  setModalVisible(true);
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Reset Password">
            <Button size="small"
              icon={<LockOutlined />}
              onClick={() =>
                handleResetPassword(
                  record.id,
                  `${record.firstname} ${record.lastname}`,
                )
              }
            />
          </Tooltip>
          {deletePermission && (
            <Popconfirm
              title={`Archive ${record.firstname} ${record.lastname}?`}
              onConfirm={() =>
                handleArchive(
                  record.id,
                  `${record.firstname} ${record.lastname}`,
                )
              }
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Archive">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* Statistics Cards */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={3}>
            <Statistic
              title="Total Users"
              value={totalUsers}
              prefix={<UserOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Statistic
              title="Admins"
              value={totalAdmins}
              prefix={<TeamOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Statistic
              title="Employees"
              value={totalEmployees}
              prefix={<TeamOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Statistic
              title="Active"
              value={totalActive}
              valueStyle={{ color: "#3f8600" }}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Statistic
              title="Inactive"
              value={totalInactive}
              valueStyle={{ color: "#cf1322" }}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Statistic
              title="Male"
              value={totalMale}
              prefix={<ManOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Statistic
              title="Female"
              value={totalFemale}
              prefix={<WomanOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Statistic
              title="Departments"
              value={totalDepartments}
              prefix={<DeploymentUnitOutlined />}
            />
          </Col>
        </Row>
      </Card>

      {/* Bulk Action Bar */}
      {selectedRowKeys.length > 0 && (
        <Card style={{ marginBottom: 16, backgroundColor: "#e6f7ff" }}>
          <Space wrap>
            <span>{selectedRowKeys.length} users selected</span>
            <Button
              icon={<LockOutlined />}
              onClick={() => openBulkModal("reset")}
            >
              Reset Password
            </Button>
            <Button
              icon={<ManOutlined />}
              onClick={() => openBulkModal("gender")}
            >
              Change Gender
            </Button>
            <Button
              icon={<TeamOutlined />}
              onClick={() => openBulkModal("role")}
            >
              Change Role
            </Button>
            <Button
              icon={<UserOutlined />}
              onClick={() => openBulkModal("status")}
            >
              Change Status
            </Button>
            <Button
              icon={<MailOutlined />}
              onClick={() => openBulkModal("message")}
            >
              Send Message
            </Button>
            <Button
              icon={<DeleteOutlined />}
              danger
              onClick={() => {
                Modal.confirm({
                  title: "Bulk Archive",
                  content: `Are you sure you want to archive ${selectedRowKeys.length} users?`,
                  onOk: async () => {
                    await api.post(`/users/bulk-archive`, {
                      userIds: selectedRowKeys,
                    });
                    message.success(`${selectedRowKeys.length} users archived`);
                    fetchUsers();
                    setSelectedRowKeys([]);
                  },
                });
              }}
            >
              Archive Selected
            </Button>
            <Button onClick={() => setSelectedRowKeys([])}>Clear</Button>
          </Space>
        </Card>
      )}

      {/* Main Card */}
      <Card
        title="User Management"
        extra={
          <Space wrap>
            <Input.Search
              placeholder="Search..."
              allowClear
              style={{ width: 260 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />

            <Select
              showSearch
              allowClear
              placeholder="Select role"
              // 🔥 search config
              optionFilterProp="label"
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              // 🔥 map API data
              options={roles.map((role) => ({
                label: role.name, // adjust if API uses different field
                value: role.id,
              }))}
              onChange={setRoleFilter}
            />

            <Select
              showSearch
              allowClear
              placeholder="Select department"
              // 🔥 search config
              optionFilterProp="label"
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              // 🔥 map API data
              options={departments.map((role) => ({
                label: role.name, // adjust if API uses different field
                value: role.id,
              }))}
              onChange={setDepartmentFilter}
            />

            <Select
              allowClear
              placeholder="User Type"
              style={{ width: 120 }}
              options={[
                { label: "Admin", value: "admin" },
                { label: "Employee", value: "employee" },
              ]}
              onChange={setTypeFilter}
            />
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 120 }}
              options={[
                { label: "Active", value: "Active" },
                { label: "Inactive", value: "Inactive" },
              ]}
              onChange={setStatusFilter}
            />
            <Select
              allowClear
              placeholder="Gender"
              style={{ width: 120 }}
              options={[
                { label: "Male", value: "Male" },
                { label: "Female", value: "Female" },
              ]}
              onChange={setGenderFilter}
            />
            <Button icon={<FileExcelOutlined />} onClick={exportToCSV}>
              CSV
            </Button>
            <Button icon={<FilePdfOutlined />} onClick={exportToPDF}>
              PDF
            </Button>
            {createPermission && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingUser(null);
                  setModalVisible(true);
                }}
              >
                Add User
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={fetchUsers}>
              Refresh
            </Button>
          </Space>
        }
      >
        <ProTable
          rowSelection={rowSelection}
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{ showSizeChanger: true, showQuickJumper: true }}
          scroll={{ x: "max-content" }}
          search={false}
          defaultSize="small"
        />
      </Card>

      {/* Bulk Action Modal */}
      <Modal
        title="Bulk Action"
        open={bulkActionModal.visible}
        onCancel={() => setBulkActionModal({ visible: false, type: null })}
        onOk={executeBulkAction}
        okText="Apply"
      >
        <Form form={bulkForm} layout="vertical">
          {bulkActionModal.type === "gender" && (
            <Form.Item
              name="gender"
              label="Select Gender"
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Radio value="Male">Male</Radio>
                <Radio value="Female">Female</Radio>
              </Radio.Group>
            </Form.Item>
          )}
          {bulkActionModal.type === "role" && (
            <Form.Item
              name="role"
              label="Select Role"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Select role"
                // 🔥 search config
                optionFilterProp="label"
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                // 🔥 map API data
                options={roles.map((role) => ({
                  label: role.name, // adjust if API uses different field
                  value: role.id,
                }))}
              />
            </Form.Item>
          )}
          {bulkActionModal.type === "status" && (
            <Form.Item
              name="status"
              label="Select Status"
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Radio value="Active">Active</Radio>
                <Radio value="Inactive">Inactive</Radio>
              </Radio.Group>
            </Form.Item>
          )}
          {bulkActionModal.type === "reset" && (
            <Text>
              Password reset links will be sent to the selected users via email.
            </Text>
          )}
          {bulkActionModal.type === "message" && (
            <>
              <Form.Item
                name="subject"
                label="Subject"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="messageText"
                label="Message"
                rules={[{ required: true }]}
              >
                <Input.TextArea rows={4} />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* Add/Edit User Modal */}
      <UserForm
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingUser(null);
        }}
        onSuccess={() => {
          setModalVisible(false);
          setEditingUser(null);
          fetchUsers();
        }}
        initialData={editingUser}
        apiUrl={apiUrl}
      />
    </>
  );
};

export default UserList;
